import {
  Button,
  Container,
  EmptyState,
  ErrorState,
  NoPermissionState,
  OrderStatusBadge,
  PageHeader,
  Skeleton,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Beer, Plus, ReceiptText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { useCurrentOutlet } from '@/features/pos/outlet';
import { openOrdersQueryOptions } from '@/features/pos/queries';
import { displayTone } from '@/features/pos/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatTime } from '@/lib/format';

export const Route = createFileRoute('/_app/pos/tabs')({ component: TabsPage });

/**
 * Comptoir (Horevia Bar, ADR-019, D-H25) : notes ouvertes du point de vente —
 * nom, ancienneté, articles, total, état — avec reprise en caisse.
 */
function TabsPage() {
  const { t, i18n } = useTranslation(['pos', 'common', 'errors']);
  const session = useSession();
  const { outlet, outlets, setOutletId, isPending: outletsPending } = useCurrentOutlet();
  const canRead = session.can('order.read');
  const canCreate = session.can('order.create');
  const orders = useQuery(openOrdersQueryOptions(outlet?.id ?? '', Boolean(outlet) && canRead));

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const list = [...(orders.data ?? [])].sort((a, b) => a.openedAt.localeCompare(b.openedAt));

  return (
    <Container>
      <PageHeader
        title={t('pos:tabs.title')}
        subtitle={t('pos:tabs.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('pos:tabs.title') },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            {outlets.length > 1 && outlet ? (
              <select
                aria-label={t('pos:bar.outlet')}
                value={outlet.id}
                onChange={(event) => setOutletId(event.target.value)}
              >
                {outlets.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            ) : null}
            {canCreate ? (
              <Button variant="primary" asChild>
                <Link to="/pos">
                  <Plus className="ico" aria-hidden="true" />
                  {t('pos:tabs.newTab')}
                </Link>
              </Button>
            ) : null}
          </div>
        }
      />

      {outletsPending || orders.isPending ? (
        <Skeleton height={200} />
      ) : !outlet ? (
        <EmptyState
          icon={<Beer className="ico" aria-hidden="true" />}
          title={t('pos:noOutlet.title')}
          description={t('pos:noOutlet.body')}
        />
      ) : orders.isError ? (
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(orders.error, t)}
          traceId={errorTraceId(orders.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void orders.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      ) : list.length === 0 ? (
        <EmptyState
          icon={<Beer className="ico" aria-hidden="true" />}
          title={t('pos:tabs.empty')}
          description={t('pos:tabs.emptyHint')}
        />
      ) : (
        <div className="tab-board" role="list" aria-label={t('pos:tabs.title')}>
          {list.map((order) => (
            <article key={order.id} className="tab-card" role="listitem">
              <div className="tab-card__head">
                <span className="tab-card__name">
                  {order.tabName ??
                    (order.tableCode
                      ? `${t('pos:newOrder.table')} ${order.tableCode}`
                      : t('pos:tabs.noName'))}
                </span>
                <OrderStatusBadge tone={displayTone(order.status, order.preparationStatus)}>
                  {t(`pos:preparation.${order.preparationStatus}`)}
                </OrderStatusBadge>
              </div>
              <div className="tab-card__meta">
                <span>#{order.dailyNumber}</span>
                <span>{t(`pos:serviceType.${order.serviceType}`)}</span>
                <span>
                  {t('pos:tabs.since', { time: formatTime(order.openedAt, i18n.language) })}
                </span>
                <span>{t('pos:tabs.items', { count: order.itemsCount })}</span>
                {order.serverName ? <span>{order.serverName}</span> : null}
              </div>
              <div className="tab-card__head">
                <span className="tab-card__total">
                  {formatAmount(order.total, i18n.language, order.currencyCode)}
                </span>
                {order.billRequestedAt ? (
                  <span className="t-12 muted">{t('pos:ticket.billRequested')}</span>
                ) : null}
              </div>
              <div className="tab-card__actions">
                <Button variant="secondary" size="sm" asChild>
                  <Link to="/pos" search={{ order: order.id }}>
                    <ReceiptText className="ico ico-16" aria-hidden="true" />
                    {t('pos:tabs.resume')}
                  </Link>
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </Container>
  );
}
